import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import RequiredSession from './RequiredSession';
import SubmittedSession from './SubmittedSession';
import { useGetProgramSessionsQuery } from '../../store/api/feedbackApi';
import { useLazyGetUserDetailsListQuery } from '../../store/api/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { userListDetails } from '../../actions/userListDetailsActions';

export interface FeedbackSessionInterface {
	userName: string;
	profession: string;
	company: string;
	programName: string;
	sessionDate: string;
}

export interface SessionInterface {
	unrated: SessionsDetailsInterface[];
	rated: SessionsDetailsInterface[];
}

export interface SessionsDetailsInterface {
	booking_date: string;
	booking_end_time: string;
	booking_participants: { id: number; full_name: string; user_id: number }[];
	booking_start_time: string;
	booking_time: string;
	mentee_id: number;
	mentor_id: number;
	group_details: {
		group_id: number;
		mentee: { full_name: string; id: number };
		mentor: { full_name: string; id: number };
	};
	id: number;
	program: { label: string; value: number };
	service: { label: string; value: number };
	session_method: string;
}

export default function PreviousSession() {
	const userListProfilesDetails = useSelector((state: RootState) => state.userListProfilesDetails);
	const loggedInUserDetail = useSelector((state: RootState) => state.loggedInUserDetails);
	const [value, setValue] = useState('1');
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [loadUserDetailsList] = useLazyGetUserDetailsListQuery();
	const { data: sessionInfo = { unrated: [], rated: [] } } = useGetProgramSessionsQuery(loggedInUserDetail.id, {
		skip: !loggedInUserDetail.id,
	});

	useEffect(() => {
		if (loggedInUserDetail.id && sessionInfo) {
			const userId = loggedInUserDetail.id;
			const filteredIds: number[] = [];
			sessionInfo.unrated?.forEach(({ mentor_id, mentee_id }: SessionsDetailsInterface) => {
				filteredIds.push(mentor_id !== userId ? mentor_id : mentee_id);
			});
			sessionInfo.rated?.forEach(({ mentor_id, mentee_id }: SessionsDetailsInterface) => {
				filteredIds.push(mentor_id !== userId ? mentor_id : mentee_id);
			});
			if (filteredIds.length > 0) {
				loadUserDetailsList(filteredIds)
					.unwrap()
					.then((users) => dispatch(userListDetails(users)));
			}
		}
	}, [dispatch, loadUserDetailsList, loggedInUserDetail.id, sessionInfo]);

	return (
		<div className="w-full">
			<header className="flex items-center border-b border-gray-200 pb-5 pt-6">
					<button aria-label="Back to feedback sessions" className="w-[20.833333%] text-center" onClick={() => navigate('../feedback-session')} type="button">
						<ArrowLeft className="mx-auto size-6" />
					</button>
					<div className="session-feedback w-[79.166667%]">Previous Session</div>
			</header>
			<Tabs onValueChange={setValue} value={value}>
				<TabsList className="h-auto w-full rounded-none border-b border-gray-200 bg-transparent p-0" variant="line">
					<TabsTrigger className="required-session py-3" value="1">Required</TabsTrigger>
					<TabsTrigger className="submitted-session py-3" value="2">Submitted</TabsTrigger>
				</TabsList>
				<TabsContent className="h-[calc(100dvh-202px)] overflow-y-auto px-4 py-6 md:h-[calc(100dvh-206px)] md:px-0" value="1">
					<RequiredSession
						loggedInUserDetail={loggedInUserDetail}
						sessionInfo={sessionInfo}
						userListProfilesDetails={userListProfilesDetails}
					/>
				</TabsContent>
				<TabsContent className="h-[calc(100dvh-202px)] overflow-y-auto px-4 py-6 md:h-[calc(100dvh-206px)] md:px-0" value="2">
					<SubmittedSession
						loggedInUserDetail={loggedInUserDetail}
						sessionInfo={sessionInfo}
						userListProfilesDetails={userListProfilesDetails}
					/>
				</TabsContent>
			</Tabs>
		</div>
	);
}
